import clsx from "clsx";
import { sortBy } from "lodash";
import React, { useMemo } from "react";
import { Experience, OPEN_UNLIMITED, Package, UnitType } from "types";
import { getMaxOpenCountFromSelectedTimeSlots } from "../../models/Purchase";
import { DemographicItem } from "./CreatePurchaseDemographicPicker";
import { getCatalogItemsByUnitType } from "@/models/Catalog";
import { getMinMaxForPackage } from "@/models/Package";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface CreatePuchaseSharedPackageDemographicPickerProps {
    packageExperiences: Experience[];
    pkg: Package;
}

export const CreatePuchaseSharedPackageDemographicPicker = React.memo(
    ({ pkg, packageExperiences }: CreatePuchaseSharedPackageDemographicPickerProps) => {
        const totalQuantity = useCreatePurchaseStore((state) => state.getActiveItem().quantity);

        const experience = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
        const demographicCatalogItems = getCatalogItemsByUnitType(pkg.catalog.items, UnitType.DEMOGRAPHIC);
        const sortedDemographicCatalogItems = useMemo(
            () => sortBy(demographicCatalogItems, "sequence"),
            [demographicCatalogItems],
        );

        let [packageMin, packageMax] = getMinMaxForPackage(pkg, packageExperiences);
        const { purchase } = useCreatePurchaseStore.getState();
        const maxOpenCount = getMaxOpenCountFromSelectedTimeSlots(purchase);
        packageMax = maxOpenCount ?? packageMax;

        const max = packageMax ?? OPEN_UNLIMITED;

        const error = totalQuantity > max ? `Quantity cannot exceed ${max}` : undefined;

        return (
            <div className="space-y-2">
                <div className={clsx("text-sm font-bold", { "text-danger": error })}>{error ?? "Quantity"}</div>
                <div className="flex flex-col items-start space-y-2">
                    {sortedDemographicCatalogItems.map((demographicCatalogItem) => {
                        return (
                            <DemographicItem
                                key={`${experience.id}${demographicCatalogItem.id}`}
                                demographicCatalogItem={demographicCatalogItem}
                                max={max}
                                experience={experience}
                                error={error}
                                min={packageMin ?? null}
                            />
                        );
                    })}
                </div>
            </div>
        );
    },
);

CreatePuchaseSharedPackageDemographicPicker.displayName = "CreatePuchaseSharedPackageDemographicPicker";
